import React, { useState, useEffect } from 'react';
import { X, UserCog, Mail, Phone, Hash, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { SearchableDropdown } from './ui/searchable-dropdown';
import { IMaskInput } from 'react-imask';
import { educationAPI } from '../api/axiosClient';
import { toast } from 'react-hot-toast';

interface SchoolUserCreateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface SchoolUserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  title: string;
  password: string;
  requirePasswordResetOnFirstLogin: boolean;
  roles: number; 
}

const countryCodes = [
  { code: '90', flag: '🇹🇷', name: 'Türkiye' },
  { code: '1', flag: '🇺🇸', name: 'Amerika' },
  { code: '44', flag: '🇬🇧', name: 'İngiltere' },
  { code: '49', flag: '🇩🇪', name: 'Almanya' },
  { code: '33', flag: '🇫🇷', name: 'Fransa' },
  { code: '39', flag: '🇮🇹', name: 'İtalya' },
  { code: '34', flag: '🇪🇸', name: 'İspanya' },
  { code: '31', flag: '🇳🇱', name: 'Hollanda' },
  { code: '32', flag: '🇧🇪', name: 'Belçika' },
  { code: '41', flag: '🇨🇭', name: 'İsviçre' },
  { code: '43', flag: '🇦🇹', name: 'Avusturya' },
  { code: '45', flag: '🇩🇰', name: 'Danimarka' },
  { code: '46', flag: '🇸🇪', name: 'İsveç' },
  { code: '47', flag: '🇳🇴', name: 'Norveç' },
  { code: '358', flag: '🇫🇮', name: 'Finlandiya' },
  { code: '48', flag: '🇵🇱', name: 'Polonya' },
  { code: '420', flag: '🇨🇿', name: 'Çekya' },
  { code: '421', flag: '🇸🇰', name: 'Slovakya' },
  { code: '36', flag: '🇭🇺', name: 'Macaristan' },
  { code: '40', flag: '🇷🇴', name: 'Romanya' },
  { code: '359', flag: '🇧🇬', name: 'Bulgaristan' },
  { code: '385', flag: '🇭🇷', name: 'Hırvatistan' },
  { code: '386', flag: '🇸🇮', name: 'Slovenya' },
  { code: '372', flag: '🇪🇪', name: 'Estonya' },
  { code: '371', flag: '🇱🇻', name: 'Letonya' },
  { code: '370', flag: '🇱🇹', name: 'Litvanya' },
  { code: '7', flag: '🇷🇺', name: 'Rusya' },
  { code: '380', flag: '🇺🇦', name: 'Ukrayna' },
  { code: '375', flag: '🇧🇾', name: 'Belarus' }
];

const SchoolUserCreateForm: React.FC<SchoolUserCreateFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const [schoolUserForm, setSchoolUserForm] = useState<SchoolUserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneCode: '90',
    phoneNumber: '',
    title: '',
    password: '',
    requirePasswordResetOnFirstLogin: true,
    roles: 5,
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Password validation
  const validateUserPassword = (password: string) => {
    const isNumeric = /^\d+$/.test(password);
    const isLength6 = password.length === 6;
    
    return {
      isValid: isNumeric && isLength6,
      isNumeric,
      isLength6
    };
  };

  const schoolUserPasswordValidation = validateUserPassword(schoolUserForm.password);

  // Form validation function
  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!schoolUserForm.firstName?.trim()) {
      newErrors.firstName = 'Ad alanı zorunludur';
    }

    if (!schoolUserForm.lastName?.trim()) {
      newErrors.lastName = 'Soyad alanı zorunludur';
    }

    if (!schoolUserForm.email?.trim()) {
      newErrors.email = 'E-posta alanı zorunludur';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(schoolUserForm.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }

    if (!schoolUserForm.phoneNumber?.trim()) {
      newErrors.phoneNumber = 'Telefon numarası alanı zorunludur';
    }

    if (!schoolUserForm.title?.trim()) {
      newErrors.title = 'Unvan alanı zorunludur';
    }

    if (![2, 5].includes(schoolUserForm.roles)) {
        newErrors.roles = 'Rol seçiniz';
        }


    if (!schoolUserForm.password?.trim()) {
      newErrors.password = 'Şifre alanı zorunludur';
    } else if (!schoolUserPasswordValidation.isValid) {
      if (!schoolUserPasswordValidation.isNumeric) {
        newErrors.password = 'Şifre sadece rakamlardan oluşmalıdır';
      } else if (!schoolUserPasswordValidation.isLength6) {
        newErrors.password = 'Şifre 6 haneli olmalıdır';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    setSchoolUserForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening
      setSchoolUserForm({
        firstName: '',
        lastName: '',
        email: '',
        phoneCode: '90',
        phoneNumber: '',
        title: '',
        password: '',
        requirePasswordResetOnFirstLogin: true,
        roles: 5,
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const cleanPhoneNumber = schoolUserForm.phoneNumber.replace(/\D/g, '');
      
      const schoolUserRequest = {
        phoneCode: schoolUserForm.phoneCode,
        phoneNumber: cleanPhoneNumber,
        email: schoolUserForm.email,
        firstName: schoolUserForm.firstName,
        lastName: schoolUserForm.lastName,
        password: schoolUserForm.password,
        title: schoolUserForm.title,
        requirePasswordResetOnFirstLogin: schoolUserForm.requirePasswordResetOnFirstLogin,
        roles: schoolUserForm.roles,
      };

      const response = await educationAPI.createSchoolUser(schoolUserRequest);

      if (response.success) {
        toast.success('Okul Kullanıcısı başarıyla eklendi');
        onSuccess();
        onClose();
      } else {
        toast.error(response.message || 'Okul Kullanıcısı eklenirken bir hata oluştu');
      }
    } catch (error: any) {
      console.error('Error creating school user:', error);
      const errorMessage = error.response?.data?.message || 'Okul Kullanıcısı eklenirken bir hata oluştu';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Yeni Okul Kullanıcısı Ekle</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Ad */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <UserCog className="w-4 h-4 text-gray-500" />
                  <span>Ad <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="text"
                  value={schoolUserForm.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                    errors.firstName ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                  }`}
                  placeholder="Adını girin"
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>
                )}
              </div>

              {/* Soyad */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <UserCog className="w-4 h-4 text-gray-500" />
                  <span>Soyad <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="text"
                  value={schoolUserForm.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                    errors.lastName ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                  }`}
                  placeholder="Soyadını girin"
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
                )}
              </div>

              {/* E-posta */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span>E-posta <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="text"
                  value={schoolUserForm.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                    errors.email ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                  }`}
                  placeholder="ornek@email.com"
                />
                {errors.email && (
                  <p className="text-sm text-red-600 mt-1">{errors.email}</p>
                )}
              </div>

              {/* Telefon */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span>Telefon <span className="text-red-500">*</span></span>
                </label>
                <div className="flex space-x-2">
                  <SearchableDropdown
                    options={countryCodes.map(code => ({
                      id: code.code,
                      label: `${code.flag} +${code.code}`,
                      value: code.code
                    }))}
                    value={countryCodes.find(country => country.code === schoolUserForm.phoneCode) ? {
                      id: schoolUserForm.phoneCode,
                      label: `${countryCodes.find(country => country.code === schoolUserForm.phoneCode)?.flag} +${schoolUserForm.phoneCode}`,
                      value: schoolUserForm.phoneCode
                    } : null}
                    onChange={(countryCode) => {
                      if (countryCode) {
                        handleInputChange('phoneCode', String(countryCode.value));
                        handleInputChange('phoneNumber', ''); // Reset phone number when country changes
                      }
                    }}
                    placeholder="Ülke kodu seç..."
                    clearable={false}
                    className="w-32"
                  />
                  <IMaskInput
                    mask="(000) 000-0000"
                    value={schoolUserForm.phoneNumber}
                    onAccept={(value) => handleInputChange('phoneNumber', value || '')}
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 text-sm ${
                      errors.phoneNumber ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                    }`}
                    placeholder="(555) 123-4567"
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="text-sm text-red-600 mt-1">{errors.phoneNumber}</p>
                )}
              </div>

              {/* Unvan */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <UserCog className="w-4 h-4 text-gray-500" />
                  <span>Unvan <span className="text-red-500">*</span></span>
                </label>
                <input
                  type="text"
                  value={schoolUserForm.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                    errors.title ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                  }`}
                  placeholder="Örn: Öğretmen, Müdür Yardımcısı"
                />
                {errors.title && (
                  <p className="text-sm text-red-600 mt-1">{errors.title}</p>
                )}
              </div>

              {/* Rol */}
                <div>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                    <UserCog className="w-4 h-4 text-gray-500" />
                    <span>Rol <span className="text-red-500">*</span></span>
                  </label>

                  <SearchableDropdown
                    options={[
                      { id: '2', label: 'Okul Yöneticisi', value: 2 },
                      { id: '5', label: 'Okul Kullanıcısı', value: 5 },
                    ]}
                    value={
                      [
                        { id: '2', label: 'Okul Yöneticisi', value: 2 },
                        { id: '5', label: 'Okul Kullanıcısı', value: 5 },
                      ].find(r => r.value === schoolUserForm.roles) || null
                    }
                    onChange={(role) => {
                      if (role) handleInputChange('roles', Number(role.value));
                    }}
                    placeholder="Rol seç..."
                    clearable={false}
                    className="w-full"
                  />

                  {errors.roles && (
                    <p className="text-sm text-red-600 mt-1">{errors.roles}</p>
                  )}
                </div>


              {/* Şifre */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Hash className="w-4 h-4 text-gray-500" />
                  <span>Şifre <span className="text-red-500">*</span></span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    maxLength={6}
                    placeholder="••••••"
                    value={schoolUserForm.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full pr-10 text-left text-sm font-mono px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition hover:border-gray-400 ${
                      errors.password ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-gray-200'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600 mt-1">{errors.password}</p>
                )}
                
                {/* Password Strength Indicator */}
                {schoolUserForm.password && (
                  <div className="space-y-2">
                    <div className="flex space-x-2">
                      <div className={`w-1/2 h-2 rounded-full ${
                        schoolUserPasswordValidation.isNumeric ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                      <div className={`w-1/2 h-2 rounded-full ${
                        schoolUserPasswordValidation.isLength6 ? 'bg-green-500' : 'bg-gray-200'
                      }`}></div>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className={`flex items-center space-x-2 ${schoolUserPasswordValidation.isNumeric ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-2 h-2 rounded-full ${schoolUserPasswordValidation.isNumeric ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span>Sadece rakam</span>
                      </div>
                      <div className={`flex items-center space-x-2 ${schoolUserPasswordValidation.isLength6 ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-2 h-2 rounded-full ${schoolUserPasswordValidation.isLength6 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span>6 haneli</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Checkbox */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="requirePasswordResetSchoolUser"
                checked={schoolUserForm.requirePasswordResetOnFirstLogin}
                onChange={(e) => setSchoolUserForm(prev => ({ ...prev, requirePasswordResetOnFirstLogin: e.target.checked }))}
                className="w-4 h-4 text-[#9B51E0] bg-gray-100 border-gray-300 rounded focus:ring-[#9B51E0] focus:ring-2"
              />
              <label htmlFor="requirePasswordResetSchoolUser" className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                <CheckCircle className="w-4 h-4 text-gray-500" />
                <span>İlk girişte şifre sıfırlama gerektir</span>
              </label>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-8 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                İptal
              </Button>
              <Button
                type="submit"
                className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Ekleniyor...
                  </>
                ) : (
                  'Kaydet'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SchoolUserCreateForm;
